<script lang="ts" setup>
import {
  ButtonAction,
  ButtonActionClass,
  ButtonSize,
  ButtonSizeClass,
} from '@/Support/Forms/Interfaces/Button'
import LoadingIcon from '@/Components/App/Global/LoadingIcon.vue'

interface Props {
  type?: 'submit' | 'reset' | 'button'
  action?: ButtonAction
  size?: ButtonSize
  loading?: boolean
  disabled?: boolean
  fullWidth?: boolean
  tag?: 'button' | 'a'
}

const props = withDefaults(defineProps<Props>(), {
  type: 'submit',
  action: ButtonAction.Primary,
  size: ButtonSize.Base,
  loading: false,
  disabled: false,
  fullWidth: false,
  tag: 'button',
})

const actionClass: ButtonActionClass = {
  primary:
    'border border-transparent text-white bg-primary-600 hover:bg-primary-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-primary-700 dark:text-neutral-200 dark:bg-primary-900 dark:hover:bg-primary-700 dark:border dark:border-neutral-600',
  warning:
    'border border-transparent text-white bg-accent-600 hover:bg-accent-600 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 dark:focus:ring-accent-900 dark:text-neutral-100 dark:bg-accent-900 dark:hover:bg-accent-700 dark:border dark:border-neutral-600 uppercase tracking-widest text-xs',
  danger:
    'border border-transparent text-white bg-danger-700 hover:bg-danger-600 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-danger-500 dark:focus:ring-danger-900 dark:text-neutral-100 dark:bg-danger-900 dark:hover:bg-danger-700 dark:border dark:border-neutral-600 uppercase tracking-widest text-xs',
  cancel:
    'border border-neutral-300 font-medium text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:text-neutral-300 dark:bg-neutral-900 dark:border dark:border-neutral-600',
  neutral:
    'border border-neutral-300 font-medium text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-neutral-500 dark:text-neutral-300 dark:bg-neutral-900 dark:border dark:border-neutral-600 rounded-none',
  inverse:
    'border border-transparent text-primary-700 bg-white hover:bg-white-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-white-500 dark:focus:ring-white-700 dark:text-neutral-800 dark:bg-primary-200 dark:hover:bg-white-700 dark:border dark:border-neutral-600',
}

const sizeClass: ButtonSizeClass = {
  extraSmall: 'px-2.5 py-1.5 text-xs font-medium rounded-sm',
  small: 'px-3 py-2 text-sm leading-4 font-medium rounded-md',
  base: 'px-4 py-2 text-sm font-medium rounded-md',
  large: 'px-4 py-2 text-base font-medium rounded-md',
  extraLarge: 'px-6 py-3 text-base font-medium rounded-md',
}
</script>
<template>
  <component
    :is="tag"
    :class="`${actionClass[action]} ${sizeClass[size]} ${
      fullWidth ? '' : 'sm:w-auto'
    }`"
    :type="type"
    :disabled="loading || disabled"
    class="inline-flex items-center justify-center shadow-xs w-full disabled:opacity-75 outline-0 disabled:cursor-not-allowed disabled:bg-neutral-400"
  >
    <slot />
    <LoadingIcon class="ml-1" :loading="loading" />
  </component>
</template>
